/**
 * Leave, schedule, holiday, and HR-leave typed fetch helpers.
 */
import type { AllLeaveResponse, ApiResponse, BulkHolidayInput, BulkHolidayResult, CreateLeaveRequestInput, DailyEventsResponse, DutyAssignInput, DutyCheckResponse, DutyScheduleMonthResponse, DutyScheduleRecord, EarnedBreakdownRecord, HolidayInput, HolidayRecord, HrAddLeaveInput, LeaveAdjustmentLogRecord, LeaveHistoryRecord, LeaveRequestRecord, LoginActivityResponse, LoginLogsResponse, MyLeaveResponse, SalaryStatsResponse, ScheduleAllResponse, ScheduleOpenInput, ScheduleRegistrationRecord, ScheduleStatusResponse, SetHrAdjustmentInput, SetRecognitionBonusInput, SpecialLeaveInput, SpecialLeaveRecord, WorkingDaysAllRow, WorkingDaysSummaryRow } from '@system/shared'
import { apiFetch } from './api-client-core'

/** GET /api/leave/my — current-year leave balance + full history for the signed-in employee. */
export async function getMyLeave(): Promise<ApiResponse<MyLeaveResponse>> {
  return apiFetch<MyLeaveResponse>('/api/leave/my')
}

/** GET /api/leave/holidays — public holiday list; optionally filter by year. */
export async function getHolidays(year?: number): Promise<ApiResponse<HolidayRecord[]>> {
  const qs = year != null ? `?year=${year}` : ''
  return apiFetch<HolidayRecord[]>(`/api/leave/holidays${qs}`)
}

/** GET /api/leave/all — all staff leave balances + recent history (HR view, auth required). */
export async function getAllLeave(year?: number): Promise<ApiResponse<AllLeaveResponse>> {
  const qs = year != null ? `?year=${year}` : ''
  return apiFetch<AllLeaveResponse>(`/api/leave/all${qs}`)
}

/** GET /api/leave/history-all — all LeaveHistory rows for the year (HR view). */
export async function getAllHistory(year?: number): Promise<ApiResponse<LeaveHistoryRecord[]>> {
  const qs = year != null ? `?year=${year}` : ''
  return apiFetch<LeaveHistoryRecord[]>(`/api/leave/history-all${qs}`)
}

/** GET /api/leave/my-requests — user's own system-Postgres leave requests. */
export async function getMyRequests(): Promise<ApiResponse<LeaveRequestRecord[]>> {
  return apiFetch<LeaveRequestRecord[]>('/api/leave/my-requests')
}

/** POST /api/leave/request — create a leave request (erp logic enforced server-side). */
export async function createLeaveRequest(input: CreateLeaveRequestInput): Promise<ApiResponse<LeaveRequestRecord>> {
  return apiFetch<LeaveRequestRecord>('/api/leave/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** POST /api/leave/request/:id/cancel — cancel own leave request, restores annual balance. */
export async function cancelLeaveRequest(id: string): Promise<ApiResponse<LeaveRequestRecord>> {
  return apiFetch<LeaveRequestRecord>(`/api/leave/request/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
  })
}

/** GET /api/leave/working-days-summary?year= — per-month working-days summary for authenticated employee. */
export async function getWorkingDaysSummary(year: number): Promise<ApiResponse<WorkingDaysSummaryRow[]>> {
  return apiFetch<WorkingDaysSummaryRow[]>(`/api/leave/working-days-summary?year=${year}`)
}

/** GET /api/leave/schedule/status — registration window + current/target month data. */
export async function getScheduleStatus(): Promise<ApiResponse<ScheduleStatusResponse>> {
  return apiFetch<ScheduleStatusResponse>('/api/leave/schedule/status')
}

/** GET /api/leave/schedule/history?year=YYYY — past registrations for signed-in employee. */
export async function getScheduleHistory(year: number): Promise<ApiResponse<ScheduleRegistrationRecord[]>> {
  return apiFetch<ScheduleRegistrationRecord[]>(`/api/leave/schedule/history?year=${year}`)
}

/** POST /api/leave/schedule/register — submit or update target month schedule. */
export async function registerSchedule(schedule: string): Promise<ApiResponse<ScheduleRegistrationRecord>> {
  return apiFetch<ScheduleRegistrationRecord>('/api/leave/schedule/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ schedule }),
  })
}

export async function hrAddLeave(input: HrAddLeaveInput): Promise<ApiResponse<LeaveHistoryRecord>> {
  return apiFetch<LeaveHistoryRecord>('/api/leave/hr-add', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** GET /api/leave/all-requests — all leave requests (HR view). */
export async function getAllRequests(): Promise<ApiResponse<LeaveRequestRecord[]>> {
  return apiFetch<LeaveRequestRecord[]>('/api/leave/all-requests')
}

/** DELETE /api/leave/request/:id — HR deletes a leave request. */
export async function hrDeleteRequest(id: string): Promise<ApiResponse<{ deleted: true }>> {
  return apiFetch<{ deleted: true }>(`/api/leave/request/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

/** POST /api/leave/holidays — create a holiday. */
export async function createHoliday(input: HolidayInput): Promise<ApiResponse<HolidayRecord>> {
  return apiFetch<HolidayRecord>('/api/leave/holidays', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** PUT /api/leave/holidays/:hID — update a holiday. */
export async function updateHoliday(hID: number, input: Partial<HolidayInput>): Promise<ApiResponse<HolidayRecord>> {
  return apiFetch<HolidayRecord>(`/api/leave/holidays/${hID}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** DELETE /api/leave/holidays/:hID — remove a holiday. */
export async function deleteHoliday(hID: number): Promise<ApiResponse<{ deleted: true }>> {
  return apiFetch<{ deleted: true }>(`/api/leave/holidays/${hID}`, { method: 'DELETE' })
}

/** GET /api/leave/working-days-all?year=&eCode= — HR batch working-days view. */
export async function getWorkingDaysAll(year: number, eCode?: string): Promise<ApiResponse<WorkingDaysAllRow[]>> {
  const params = new URLSearchParams({ year: String(year) })
  if (eCode) params.set('eCode', eCode)
  return apiFetch<WorkingDaysAllRow[]>(`/api/leave/working-days-all?${params}`)
}

/** GET /api/leave/schedule/all?year= — HR view: open windows + registrations. */
export async function getScheduleAll(year: number): Promise<ApiResponse<ScheduleAllResponse>> {
  return apiFetch<ScheduleAllResponse>(`/api/leave/schedule/all?year=${year}`)
}

/** POST /api/leave/schedule/open — create or update open-registration window. */
export async function openScheduleWindow(input: ScheduleOpenInput): Promise<ApiResponse<{ year: number; month: number; deadline: string; openedAt: string }>> {
  return apiFetch('/api/leave/schedule/open', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** DELETE /api/leave/schedule/open/:year/:month — close registration window. */
export async function closeScheduleWindow(year: number, month: number): Promise<ApiResponse<{ deleted: true }>> {
  return apiFetch<{ deleted: true }>(`/api/leave/schedule/open/${year}/${month}`, { method: 'DELETE' })
}

/** GET /api/leave/special — list all special-leave records. */
export async function getSpecialLeave(): Promise<ApiResponse<SpecialLeaveRecord[]>> {
  return apiFetch<SpecialLeaveRecord[]>('/api/leave/special')
}

/** POST /api/leave/special — enable, update or disable special leave. */
export async function upsertSpecialLeave(input: SpecialLeaveInput): Promise<ApiResponse<SpecialLeaveRecord | { deleted: boolean }>> {
  return apiFetch('/api/leave/special', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** GET /api/leave/adjustment-logs?limit= — newest-first audit log. */
export async function getAdjustmentLogs(limit = 50): Promise<ApiResponse<LeaveAdjustmentLogRecord[]>> {
  return apiFetch<LeaveAdjustmentLogRecord[]>(`/api/leave/adjustment-logs?limit=${limit}`)
}

/** POST /api/leave/holidays/bulk — G9: bulk import holidays (idempotent, skips duplicates). */
export async function bulkImportHolidays(input: BulkHolidayInput): Promise<ApiResponse<BulkHolidayResult>> {
  return apiFetch<BulkHolidayResult>('/api/leave/holidays/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** GET /api/leave/earned-breakdown?year=&month= — G11: earned-days formula breakdown for signed-in employee. */
export async function getEarnedBreakdown(year: number, month: number): Promise<ApiResponse<EarnedBreakdownRecord>> {
  return apiFetch<EarnedBreakdownRecord>(`/api/leave/earned-breakdown?year=${year}&month=${month}`)
}

/** POST /api/leave/recognition-bonus — G11: HR sets recognition bonus for an employee's balance. */
export async function setRecognitionBonus(input: SetRecognitionBonusInput): Promise<ApiResponse<{ updated: true }>> {
  return apiFetch<{ updated: true }>('/api/leave/recognition-bonus', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** POST /api/leave/hr-adjustment — HR sets the signed correction (negative = leave taken in advance). */
export async function setHrAdjustment(input: SetHrAdjustmentInput): Promise<ApiResponse<{ updated: true }>> {
  return apiFetch<{ updated: true }>('/api/leave/hr-adjustment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

// ── G10: Duty Schedule ────────────────────────────────────────────────────

/** GET /api/leave/duty/check — self-check if signed-in user is on duty today. */
export async function checkDutyToday(): Promise<ApiResponse<DutyCheckResponse>> {
  return apiFetch<DutyCheckResponse>('/api/leave/duty/check')
}

/** GET /api/leave/duty?year=&month= — HR: month roster + active employees list. */
export async function getDutyRoster(year: number, month: number): Promise<ApiResponse<DutyScheduleMonthResponse>> {
  return apiFetch<DutyScheduleMonthResponse>(`/api/leave/duty?year=${year}&month=${month}`)
}

/** POST /api/leave/duty — HR: upsert day assignment (max 2 employees, no duplicates). */
export async function assignDuty(input: DutyAssignInput): Promise<ApiResponse<DutyScheduleRecord>> {
  return apiFetch<DutyScheduleRecord>('/api/leave/duty', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** DELETE /api/leave/duty/:date — HR: clear duty assignment for a specific day. */
export async function clearDutyDay(date: string): Promise<ApiResponse<{ cleared: true }>> {
  return apiFetch<{ cleared: true }>(`/api/leave/duty/${encodeURIComponent(date)}`, {
    method: 'DELETE',
  })
}

// ── G12: Salary Stats ────────────────────────────────────────────────────────

/** GET /api/leave/salary-stats?year=&month= — HR: per-employee salary stats (mirrors erp salary-stats.php). */
export async function getSalaryStats(year: number, month: number): Promise<ApiResponse<SalaryStatsResponse>> {
  return apiFetch<SalaryStatsResponse>(`/api/leave/salary-stats?year=${year}&month=${month}`)
}

// ── G12: Daily Events (skeleton) ─────────────────────────────────────────────

/** GET /api/leave/daily-events — HR daily events (challenges + food orders). Returns dataGap=true until ETL is wired. */
export async function getDailyEvents(): Promise<ApiResponse<DailyEventsResponse>> {
  return apiFetch<DailyEventsResponse>('/api/leave/daily-events')
}

// ── G12: Login Logs ───────────────────────────────────────────────────────────

/** GET /api/leave/login-logs — HR: user login history from Session table. */
export async function getLoginLogs(params: {
  year: number
  month: number
  email?: string
  dateFrom?: string
  dateTo?: string
}): Promise<ApiResponse<LoginLogsResponse>> {
  const qs = new URLSearchParams({ year: String(params.year), month: String(params.month) })
  if (params.email)    qs.set('email',     params.email)
  if (params.dateFrom) qs.set('date_from', params.dateFrom)
  if (params.dateTo)   qs.set('date_to',   params.dateTo)
  return apiFetch<LoginLogsResponse>(`/api/leave/login-logs?${qs}`)
}

/** GET /api/leave/login-logs/activity — one user's page views on one local day, by session. */
export async function getLoginActivity(email: string, date: string): Promise<ApiResponse<LoginActivityResponse>> {
  const qs = new URLSearchParams({ email, date })
  return apiFetch<LoginActivityResponse>(`/api/leave/login-logs/activity?${qs}`)
}
